import { useMultilingualService } from '../service/multilingualService'
import fieldLevelErrorMapping from '../constants/inlineErrors'
import { NON_RECOVERABLE_ERROR_MAPPINGS } from './nonRecoverableErrorMapping'
import { systemErrorCache } from '../context/appCache'

/**
 * Get error object based on error code return from API
 * @param {Object} error - gqlErrors from API
 * @param {String} pageName - pagename for error messages (ex: add/edit etc)
 *
 * @returns {Object}
 */
export const getErrorData = ({ error, pageName }) => {
  const errorCode = Array.isArray(error)
    ? error?.[0]?.extensions?.errorCode || error?.[0]?.errorCode
    : error?.extensions?.errorCode || error?.[0]?.errorCode
  if (errorCode) {
    const errors = useMultilingualService(pageName)
    if (errors && errors[errorCode] && fieldLevelErrorMapping[errorCode]) {
      return {
        message: errors[errorCode],
        fieldErrorData: {
          shouldShowFieldLevelError: true,
          ...fieldLevelErrorMapping[errorCode]
        },
        hasSystemError: fieldLevelErrorMapping[errorCode]?.hasSystemError
      }
    }
    if (errors && errors[errorCode]) {
      return {
        message: errors[errorCode],
        fieldErrorData: { shouldShowFieldLevelError: false }
      }
    }
    return {
      message: '',
      fieldErrorData: { shouldShowFieldLevelError: false }
    }
  }
  return {
    message: error?.toString(),
    fieldErrorData: { shouldShowFieldLevelError: false }
  }
}

/**
 * To check if the error code returned from API is belongs to generic error or not
 * @param {Object} error - gqlErrors from API
 *
 * @returns {Boolean}
 */
export const isGenericError = ({ error }) => {
  const errorCode = Array.isArray(error)
    ? error?.[0]?.extensions?.errorCode || error?.[0]?.errorCode
    : error?.extensions?.errorCode || error?.[0]?.errorCode
  const errorMapping = NON_RECOVERABLE_ERROR_MAPPINGS[errorCode]
  if (errorMapping?.hasSystemErrorIssue) {
    systemErrorCache({
      errorMsg: errorMapping?.errorMsg,
      CTA: errorMapping?.CTA,
      isRecoverable: errorMapping?.isRecoverable,
      title: errorMapping?.title
    })
  } else {
    systemErrorCache({})
  }
  if (
    (errorCode &&
      !errorCode.startsWith('500.') &&
      !errorCode.startsWith('400.') &&
      !errorMapping?.hasSystemErrorIssue) ||
    (!error?.statusCode && !errorMapping?.hasSystemErrorIssue)
  ) {
    return false
  }
  return true
}
/**
 * To check if the error code returned from API is freeze the user to get subscriber status invalid error
 * @param {Object} error - gqlErrors from API
 *
 * @returns {Boolean}
 */
export const isAccessDenied = ({ error }) => {
  const errorCode = error?.extensions?.errorCode
  if (errorCode && ['409.6666.4239']?.includes(errorCode)) {
    return true
  }
  return false
}
export const accessDeniedErrorCodes = ['409.6666.4239']
/**
 * Get error message for form
 * @param {String} value - input field name
 * @param {String} pageName - pagename for error messages (ex: add/edit etc)
 *
 * @returns {String}
 */
export const errorMessageMappingForForm = ({
  value,
  pageName,
  companyTab,
  input
}) => {
  const { DETAILS, CONTACT } = useMultilingualService(pageName)
  const phoneNumberErrorText = companyTab
    ? CONTACT.ADD_BILL_PHONR_ERROR_COMPANY
    : CONTACT?.ADD_BILL_PHONR_ERROR_PERSON
  if (
    pageName === 'editBill' &&
    (value === 'company' || value === 'personName')
  ) {
    return DETAILS.EDIT_BILL_COMPANY_NAME_ERROR
  }

  switch (value) {
    case 'company':
      return DETAILS.ADD_BILL_COMPANY_NAME_ERROR
    case 'person':
      return DETAILS.ADD_BILL_PERSON_NAME_ERROR
    case 'accountNumber':
      if (input) {
        return DETAILS.SEARCH_ACCOUNT_ERROR_INPUT
      }
      return pageName === 'addBillManual'
        ? DETAILS.ADD_BILL_ACCOUNT_ERROR
        : DETAILS.SEARCH_ACCOUNT_ERROR
    case 'phoneNumber':
      return pageName === 'addBillManual'
        ? phoneNumberErrorText
        : CONTACT.EDIT_PHONR_ERROR_DEFAULT
    case 'address1':
      return CONTACT.STREET_ERROR_DEFAULT
    case 'city':
      return CONTACT.CITY_ERROR_DEFAULT
    case 'state':
      return CONTACT.STATE_ERROR_DEFAULT
    case 'zipCode':
      return input ? CONTACT.ZIPCODE_ERROR_PAGELEVEL : ''
    default:
      return ''
  }
}

/**
 * To check if the error returned for API will be applicable for field or not
 * @param {Object} fieldErrorData - error object for field
 * @param {String} field - field name (ex: city/state/zipcode)
 *
 * @returns {Boolean}
 */
export const isErrorApplicableForField = (fieldErrorData, field) =>
  fieldErrorData &&
  fieldErrorData.filedName &&
  fieldErrorData.filedName === field

/**
 * To get the corresponding field level error meesage
 * @param {Object} defaultMessage - default error message for field
 * @param {Object} fieldErrorData - error object for field
 * @param {String} field - field name (ex: city/state/zipcode)
 * @param {String} pageName - pagename for error messages (ex: add/edit etc)
 *
 * @returns {String}
 */
export const getFieldErrorMessage = (
  defaultMessage,
  fieldErrorData,
  field,
  pageName
) => {
  if (isErrorApplicableForField(fieldErrorData, field)) {
    const errors = useMultilingualService(pageName)
    return errors[fieldErrorData.messageKey]
  }
  return defaultMessage
}

/**
 * To get the corresponding field level error meesage
 * @param {Object} fieldErrorData - error object for field
 * @param {String} field - field name (ex: city/state/zipcode)
 * @param {String} errorCount - error count for the field
 *
 * @returns {String}
 */
export const incrementErrorCountsForFieldLevelError = (
  fieldErrorData,
  field,
  errorCount
) => {
  if (isErrorApplicableForField(fieldErrorData, field)) {
    return 1
  }
  return errorCount
}

export default getErrorData
